import { Injectable, OnDestroy, signal } from '@angular/core';
import {
  LoginAdminResponseApi,
  PermissionApi,
  UserAdminLoginApi,
} from '../../features/auth/models/auth-api.model';

const SESSION_KEY = 'admin_session';
const ACCESS_TOKEN_COOKIE = 'access_token';
const REFRESH_TOKEN_COOKIE = 'refresh_token';
const REFRESH_TOKEN_MAX_AGE = 30 * 24 * 60 * 60;

interface StoredSession {
  user: UserAdminLoginApi;
  menu: PermissionApi[];
}

export function isSuperAdministrator(user: UserAdminLoginApi | null): boolean {
  const roleName = user?.role.name.trim().toUpperCase();
  return roleName === 'ROLE_ADMIN' || roleName === 'SUPER ADMINISTRADOR';
}

@Injectable({ providedIn: 'root' })
export class AuthSessionService implements OnDestroy {
  readonly user = signal<UserAdminLoginApi | null>(null);
  readonly menu = signal<PermissionApi[]>([]);

  // Los tokens se restauran desde cookies para que una recarga no envíe la
  // primera petición protegida sin Authorization y pueda renovar la sesión.
  readonly accessToken = signal<string | null>(null);
  readonly refreshToken = signal<string | null>(null);

  constructor() {
    this.restore();
    // Sync entre pestañas: localStorage.setItem/removeItem en OTRA pestaña
    // dispara este evento aca (nunca en la pestaña que hizo el cambio). Para
    // cuando esta pestaña se entera, el valor ya esta actualizado en el
    // storage compartido, asi que basta con releer el mismo estado.
    window.addEventListener('storage', this.onStorageChange);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorageChange);
  }

  setSession(response: LoginAdminResponseApi): void {
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        user: response.user,
        menu: response.menu,
      } satisfies StoredSession),
    );
    this.writeAccessTokenCookie(response.token.access_token, response.token.expires_in);
    this.writeCookie(REFRESH_TOKEN_COOKIE, response.token.refresh_token, REFRESH_TOKEN_MAX_AGE);
    this.accessToken.set(response.token.access_token);
    this.refreshToken.set(response.token.refresh_token);
    this.user.set(response.user);
    this.menu.set(response.menu);
  }

  updateAccessToken(accessToken: string, expiresIn?: number): void {
    this.writeAccessTokenCookie(accessToken, expiresIn);
    this.accessToken.set(accessToken);
  }

  updateTokens(token: { access_token: string; refresh_token?: string; expires_in?: number }): void {
    this.updateAccessToken(token.access_token, token.expires_in);
    if (token.refresh_token) {
      this.writeCookie(REFRESH_TOKEN_COOKIE, token.refresh_token, REFRESH_TOKEN_MAX_AGE);
      this.refreshToken.set(token.refresh_token);
    }
  }

  clear(): void {
    localStorage.removeItem(SESSION_KEY);
    this.deleteAccessTokenCookie();
    this.deleteCookie(REFRESH_TOKEN_COOKIE);
    this.resetSignals();
  }

  private readonly onStorageChange = (event: StorageEvent): void => {
    if (event.key === SESSION_KEY) this.restore();
  };

  private restore(): void {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) {
      this.deleteAccessTokenCookie();
      this.deleteCookie(REFRESH_TOKEN_COOKIE);
      this.resetSignals();
      return;
    }

    try {
      const stored = JSON.parse(raw) as StoredSession;
      const user = stored?.user;
      const isValidUser =
        typeof user === 'object' &&
        user !== null &&
        typeof user.idUser === 'number' &&
        typeof user.email === 'string';
      if (!Array.isArray(stored?.menu) || !isValidUser) {
        throw new Error('Formato de sesión inválido');
      }
      this.user.set(stored.user);
      this.menu.set(stored.menu);
      this.accessToken.set(this.readAccessTokenCookie());
      this.refreshToken.set(this.readCookie(REFRESH_TOKEN_COOKIE));
    } catch {
      localStorage.removeItem(SESSION_KEY);
      this.deleteAccessTokenCookie();
      this.deleteCookie(REFRESH_TOKEN_COOKIE);
      this.resetSignals();
    }
  }

  private resetSignals(): void {
    this.accessToken.set(null);
    this.refreshToken.set(null);
    this.user.set(null);
    this.menu.set([]);
  }

  private readAccessTokenCookie(): string | null {
    return this.readCookie(ACCESS_TOKEN_COOKIE);
  }

  private readCookie(name: string): string | null {
    const prefix = `${name}=`;
    const cookie = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));
    if (!cookie) return null;

    try {
      return decodeURIComponent(cookie.slice(prefix.length));
    } catch {
      this.deleteCookie(name);
      return null;
    }
  }

  private writeAccessTokenCookie(accessToken: string, expiresIn?: number): void {
    const maxAge = Number.isFinite(expiresIn) && expiresIn! > 0 ? Math.floor(expiresIn!) : undefined;
    this.writeCookie(ACCESS_TOKEN_COOKIE, accessToken, maxAge);
  }

  private writeCookie(name: string, value: string, maxAge?: number): void {
    const maxAgeAttribute = maxAge ? `; Max-Age=${maxAge}` : '';
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${name}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${maxAgeAttribute}${secure}`;
  }

  private deleteAccessTokenCookie(): void {
    this.deleteCookie(ACCESS_TOKEN_COOKIE);
  }

  private deleteCookie(name: string): void {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  }
}
